import React, { useEffect, useMemo, useState } from 'react';
import masterDataService from '../../service/masterData.service';
import { generateAutocompleteItems } from '../../utils/helper';
import useMenus from '../../context/MenuContext';
import useUsers from '../../context/UsersContext';
import useParam from '../../context/ParamContext';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';

import { AutoCompleteSelectUI, ButtonUI, FormLabelUI } from '../Interface';
import JobMonitorCard from './JobMonitorCard';
import { Box, Typography, useMediaQuery } from '@mui/material';
import ServiceDialog from '../ServiceCore/ServiceDialog';
import serviceCoreService from '../../service/serviceCore.service';
import JobMonitorWorkAreas from './JobMonitorWorkAreas';
import ButtonDialog from './ButtonDialog';

export const ServiceJobMonitorTaskAssigmentPage = () => {
  const { getUser, user } = useUsers();
  const isMobile = useMediaQuery('(max-width:960px)');
  const { getParam } = useParam();
  const saveView = useSaveView(user);
  const serviceJobMonitorTaskAssigmentFilter = useView('serviceJobMonitorTaskAssigmentFilter', user);
  const { menus, getMenus, getMenuSubMenuId } = useMenus();
  const [serviceCores, setServiceCores] = useState({ rows: [], rowCount: 0 });
  const [draggingItemId, setDraggingItemId] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState({ resourceId: null, index: null });
  const [autocompleteOptions, setAutoCompleteOptions] = useState({
    type: [],
    operations: [],
    groups: [],
    workAreas: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [workAreas, setWorkAreas] = useState([]);
  const [isInitialLoadDone, setIsInitialLoadDone] = useState(false);
  const [serviceDialogOpen, setServiceDialogOpen] = useState(false);
  const [service, setService] = useState({});
  const [serviceChanged, setServiceChanged] = useState(false);

  const defBtnDialogData = {
    open: false,
    buttons: [],
    headerContent: '',
    onClick: () => {},
  };
  const [btnDialogData, setBtnDialogData] = useState(defBtnDialogData);

  const inititalState = {
    filter: {
      filterModel: {
        items: [],
        logicOperator: 'and',
      },
    },
    sorting: {
      sortModel: [{ field: 's_workingNumber', sort: 'asc' }],
    },
  };
  const [query, setQuery] = useState(inititalState);

  useEffect(() => {
    getMenus();
    getParam();
    if (!Object.keys(user).length) getUser();
  }, []);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      getList();
      getWorkAreas();
    }
  }, [query?.filter?.filterModel?.items, user]);

  useEffect(() => {
    if (serviceChanged) {
      getList();
      getWorkAreas();
    }
  }, [serviceChanged]);

  useEffect(() => {
    if (!Object.keys(menus).length) return;
    initDefaultValues();
  }, [menus]);

  useEffect(() => {
    if (Object.keys(serviceJobMonitorTaskAssigmentFilter).length > 0 && !isInitialLoadDone) {
      const filters = Object.entries(serviceJobMonitorTaskAssigmentFilter).map(([field, value]) => ({ field, value }));
      if (filters.length > 0) {
        handleFilterChange(filters);
      }

      setIsInitialLoadDone(true);
    }
  }, [serviceJobMonitorTaskAssigmentFilter, isInitialLoadDone]);

  const initDefaultValues = () => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .serviceOperationToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    const submenuId = getMenuSubMenuId('workArea');
    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .groupToForm(submenuId)
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .workAreaToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          type: resp[0],
          operations: resp[1],
          groups: resp[2],
          workAreas: resp[3],
        };

        setAutoCompleteData(aData);

        const acOptions = {
          type: generateAutocompleteItems(aData.type, 'name', 'id'),
          operations: generateAutocompleteItems(aData.operations, 'name', 'name'),
          groups: generateAutocompleteItems(aData.groups.projectGroupListitems, 'itemName', 'itemId'),
          workAreas: generateAutocompleteItems(aData.workAreas, 'name', 'id'),
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {});
  };

  const getList = () => {
    const cleanedQuery = {
      ...query,
      filter: {
        filterModel: {
          ...query.filter.filterModel,
          items: query.filter.filterModel.items.filter((item) => item.field !== 'workAreaGroupId'),
        },
      },
    };

    serviceCoreService
      .getItemsFromView(cleanedQuery)
      .then((data) => {
        setServiceCores(data);
        setServiceChanged(false);
      })
      .finally(() => {});
  };

  const getWorkAreas = () => {
    const data = {
      ...query,
      sorting: {
        sortModel: [{ field: 'name', sort: 'asc' }],
      },
    };

    serviceCoreService
      .getWorkAreasFromView(data)
      .then((data) => {
        setWorkAreas(data);
        setServiceChanged(false);
      })
      .finally(() => {});
  };

  const handleFilterChange = (field, value) => {
    setQuery((prev) => {
      let newItems = prev.filter.filterModel.items;

      if (Array.isArray(field)) {
        newItems = newItems.filter((item) => !field.some((f) => f.field === item.field));

        const validFilters = field
          .filter((f) => {
            const v = f.value;
            return v !== null && v !== undefined && v !== '' && (!Array.isArray(v) || v.length > 0);
          })
          .map((f) => {
            const rawValue = f.value?.value ?? f.value;
            const processedValue = Array.isArray(rawValue) ? rawValue.map((v) => v.value ?? v) : rawValue;

            return {
              field: f.field,
              operator: Array.isArray(processedValue) ? 'isAnyOf' : 'contains',
              value: processedValue,
            };
          });

        newItems = newItems.concat(validFilters);
      } else {
        newItems = newItems.filter((item) => item.field !== field);
        if (value !== null && value !== undefined && value !== '') {
          const processedValue = Array.isArray(value) ? value.map((v) => v.value ?? v) : value;

          newItems.push({
            field,
            operator: Array.isArray(processedValue) ? 'isAnyOf' : 'contains',
            value: processedValue,
          });
        }
      }

      return {
        ...prev,
        filter: {
          filterModel: {
            items: newItems,
          },
        },
      };
    });
  };

  const sameRow = (a, b) => a.s_id === b.s_id;

  const updateStateAfterAssignment = (jobItem, newWorkAreaId) => {
    setServiceCores((prev) => ({
      ...prev,
      rows: prev.rows.filter((r) => !sameRow(r, jobItem)),
    }));

    setWorkAreas((prev) =>
      prev.map((group) => {
        if (group.workAreaId === jobItem.workAreaId) {
          const rows = group.rows.filter((r) => !sameRow(r, jobItem));
          const currentTotal = Number(group.s_total_plannedtime ?? 0);
          const delta = Number(jobItem?.s_total_plannedtime ?? jobItem?.s_plannedtime ?? 0);

          return {
            ...group,
            rows,
            s_total_plannedtime: (currentTotal - delta).toFixed(2),
          };
        }

        if (group.workAreaId === newWorkAreaId) {
          const updatedRow = { ...jobItem, workAreaId: newWorkAreaId };
          const withoutOld = group.rows.filter((r) => !sameRow(r, jobItem));
          const currentTotal = Number(group.s_total_plannedtime ?? 0);
          const delta = Number(jobItem?.s_total_plannedtime ?? jobItem?.s_plannedtime ?? 0);

          return {
            ...group,
            rows: [updatedRow, ...withoutOld],
            s_total_plannedtime: (currentTotal + delta).toFixed(2),
          };
        }

        return group;
      })
    );
  };

  const updateStateToUnassigned = (jobItem, workAreaId) => {
    const updatedRow = { ...jobItem, workAreaId };
    setServiceCores((prev) => {
      const exists = prev.rows.some((r) => sameRow(r, updatedRow));
      return { ...prev, rows: exists ? prev.rows : [updatedRow, ...prev.rows] };
    });
    setWorkAreas((prev) =>
      prev.map((group) =>
        group.workAreaId === jobItem.workAreaId
          ? { ...group, rows: group.rows.filter((r) => !sameRow(r, jobItem)) }
          : group
      )
    );
  };

  const handleDropAndWorkAreaUpdate = (workAreaId, jobItem) => {
    serviceCoreService.updateServiceWorkArea(jobItem.s_id, workAreaId).then(() => {
      updateStateAfterAssignment(jobItem, workAreaId);
    });
  };

  const onDragStart = (event, jobItem) => {
    event.dataTransfer.setData('jobItem', JSON.stringify(jobItem));
    setDraggingItemId(jobItem.s_id);
  };

  const onDrop = (event, workArea) => {
    event.preventDefault();

    const jobItem = JSON.parse(event.dataTransfer.getData('jobItem'));

    handleDropAndWorkAreaUpdate(workArea.workAreaId, jobItem);
  };

  const onDropToUnassigned = (event) => {
    event.preventDefault();

    const jobItem = JSON.parse(event.dataTransfer.getData('jobItem'));

    const workAreaId = null;

    serviceCoreService
      .updateServiceWorkArea(jobItem.s_id, workAreaId)
      .then(() => updateStateToUnassigned(jobItem, workAreaId));
  };

  const getServiceById = (id) => {
    setServiceDialogOpen(true);
    serviceCoreService.getItem(id).then((data) => {
      setService(data);
    });
  };

  const handleCloseBtnDialog = () => {
    setBtnDialogData(defBtnDialogData);
  };

  const projectTypeLabel = serviceJobMonitorTaskAssigmentFilter?.s_projectTypeId?.label;

  return (
    <div className="bg-[#E9F5FE] px-6">
      {isMobile ? (
        <Box py={1.5} display="flex" gap={1}>
          <ButtonUI
            className="bg-primary px-2"
            text={`Projekt típusok ${projectTypeLabel ? `(${projectTypeLabel})` : ''}`}
            size="sm"
            onClick={() =>
              setBtnDialogData({
                open: true,
                headerContent: 'Projekt típusok',
                buttons: autoCompleteData.type,
                onClick: (type) => {
                  handleFilterChange('s_projectTypeId', type.id);
                  saveView('serviceJobMonitorTaskAssigmentFilter', {
                    s_projectTypeId: { label: type.name, value: type.id },
                  });
                  handleCloseBtnDialog();
                },
              })
            }
          />
          <ButtonUI
            className="bg-warning px-2"
            text="Összes szűrő leürítése"
            size="sm"
            disabled={query.filter.filterModel.items.length === 0}
            onClick={() => {
              setQuery(inititalState);
              saveView('serviceJobMonitorTaskAssigmentFilter', {
                s_projectTypeId: null,
                workAreaGroupId: null,
                operationName: null,
              });
            }}
          />
        </Box>
      ) : (
        <div className="flex justify-between gap-5 flex-wrap">
          <div className="flex gap-x-5 flex-wrap mb-5">
            <div className="w-[250px]">
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Projekt típus" />}
                dataset={autocompleteOptions.type}
                onChange={(_e, newVal, reason) => {
                  handleFilterChange('s_projectTypeId', reason === 'clear' ? null : newVal?.value);
                  saveView('serviceJobMonitorTaskAssigmentFilter', { s_projectTypeId: newVal });
                }}
                selectedValue={query.filter.filterModel.items.find((item) => item.field === 's_projectTypeId')?.value}
                selectedLabelValue={projectTypeLabel}
              />
            </div>
            <div className="w-[250px]">
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Munkaállomás csoport" />}
                dataset={autocompleteOptions.groups}
                onChange={(_e, newVal, reason) => {
                  handleFilterChange('workAreaGroupId', reason === 'clear' ? null : newVal?.value);
                  saveView('serviceJobMonitorTaskAssigmentFilter', { workAreaGroupId: newVal });
                }}
                selectedValue={query.filter.filterModel.items.find((item) => item.field === 'workAreaGroupId')?.value}
                selectedLabelValue={serviceJobMonitorTaskAssigmentFilter?.workAreaGroupId?.label}
              />
            </div>
            <div className="w-[250px]">
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Szerviz tevékenység" />}
                dataset={autocompleteOptions.operations}
                onChange={(_e, newVal, reason) => {
                  handleFilterChange('operationName', reason === 'clear' ? null : newVal?.value);
                  saveView('serviceJobMonitorTaskAssigmentFilter', { operationName: newVal });
                }}
                selectedValue={query.filter.filterModel.items.find((item) => item.field === 'operationName')?.value}
                selectedLabelValue={serviceJobMonitorTaskAssigmentFilter?.operationName?.label}
              />
            </div>
          </div>
        </div>
      )}
      <div className="grid grid-cols-12 gap-3">
        <div
          id="scrollableDiv"
          className={`${isMobile ? 'col-span-12 min-h-[calc(100vh-80px)] ' : 'col-span-4 min-h-[calc(100vh-135px)] '}   max-h-[calc(100vh-135px)] overflow-y-scroll custom-scrollbar bg-gray-100`}
          onDragOver={(e) => e.preventDefault()}
          onDrop={onDropToUnassigned}
        >
          <div className="bg-white mb-3 p-2 text-labelColor">
            <Typography fontWeight="bold" fontSize="large">
              {`Tervezett idő összesen: ${(serviceCores.rows ?? [])
                .reduce((sum, op) => sum + Number(op.s_total_plannedtime ?? 0), 0)
                .toFixed(1)} óra`}
            </Typography>
          </div>

          {serviceCores.rows?.map((operation) => (
            <JobMonitorCard
              key={operation.s_id}
              data={operation}
              onDragStart={onDragStart}
              autoCompleteData={autoCompleteData}
              getServiceById={getServiceById}
              isMobile={isMobile}
              onClick={
                isMobile
                  ? (data) =>
                      setBtnDialogData({
                        open: true,
                        headerContent: 'Munkaállomások',
                        buttons: autoCompleteData.workAreas,
                        onClick: (workArea) => {
                          handleDropAndWorkAreaUpdate(workArea.id, data);
                          handleCloseBtnDialog();
                        },
                      })
                  : undefined
              }
            />
          ))}
        </div>
        {!isMobile && (
          <div className="col-span-8 min-h-[calc(100vh-135px)] max-h-[calc(100vh-135px)] overflow-x-auto overflow-hidden custom-scrollbar">
            <div className="flex justify-start items-start gap-1 pt-0">
              {workAreas?.map((workArea) => (
                <div className="min-w-[350px] max-w-[350px]" key={workArea.workAreaId}>
                  <JobMonitorWorkAreas
                    workArea={workArea}
                    onDragStart={onDragStart}
                    onDrop={onDrop}
                    isDragging={{ draggingItemId, setDraggingItemId }}
                    dragOverData={{ dragOverIndex, setDragOverIndex }}
                    autoCompleteData={autoCompleteData}
                    getServiceById={getServiceById}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <ButtonDialog handleClose={() => handleCloseBtnDialog()} dialogData={btnDialogData} />

      {serviceDialogOpen && (
        <ServiceDialog
          showDialog={serviceDialogOpen}
          onClose={() => setServiceDialogOpen(false)}
          data={service}
          changed={{ serviceChanged, setServiceChanged }}
        />
      )}
    </div>
  );
};
